import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Flag, Calendar, Pencil } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, isBefore, startOfToday } from "date-fns";
import DatePicker from "@/components/DatePicker";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";

const PRIORITIES = [
  { v: "urgent", c: "#ef4444", label: "Urgent" },
  { v: "high", c: "#F05A28", label: "High" },
  { v: "medium", c: "#eab308", label: "Medium" },
  { v: "low", c: "#3b82f6", label: "Low" },
  { v: "none", c: "#71717A", label: "None" },
];
const STATUSES = [
  { v: "todo", label: "To do" },
  { v: "in_progress", label: "In progress" },
  { v: "done", label: "Done" },
];

function TaskRow({ task, onToggle, onDelete, onEdit }) {
  const overdue = task.due_date && task.status !== "done" && isBefore(parseISO(task.due_date), startOfToday());
  const pri = PRIORITIES.find(p => p.v === task.priority);
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/[0.02]" data-testid={`task-row-${task.task_id}`}>
      <Checkbox checked={task.status === "done"} onCheckedChange={() => onToggle(task)} data-testid={`task-check-${task.task_id}`} />
      <button onClick={() => onEdit(task)} className="flex-1 text-left">
        <div className={`text-sm ${task.status === "done" ? "line-through text-white/40" : "text-white/90"}`}>{task.title}</div>
        <div className="flex items-center gap-3 mt-1 text-xs text-white/40">
          {task.priority !== "none" && <span className="flex items-center gap-1"><Flag size={10} style={{ color: pri?.c }} /> {pri?.label}</span>}
          {task.due_date && <span className={`flex items-center gap-1 ${overdue ? "text-[--orange]" : ""}`} style={overdue ? { color: "var(--orange)" } : {}}><Calendar size={10} /> {format(parseISO(task.due_date), "MMM d")}</span>}
          {task.tags?.map(t => <span key={t}>#{t}</span>)}
        </div>
      </button>
      <button onClick={() => onDelete(task)} className="text-white/30 hover:text-white/70"><Trash2 size={14} /></button>
    </div>
  );
}

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [view, setView] = useState("today");
  const [filterProject, setFilterProject] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", priority: "none", status: "todo", due_date: "", project_id: "", tags: "" });

  const load = async () => {
    const params = {};
    if (view === "today") params.due = "today";
    if (view === "overdue") params.due = "overdue";
    if (view === "week") params.due = "week";
    if (view === "inbox") params.due = "inbox";
    if (filterProject !== "all" && !["today", "overdue", "week", "inbox"].includes(view)) params.project_id = filterProject;
    setTasks((await api.get("/tasks", { params })).data);
  };
  const loadProjects = async () => setProjects((await api.get("/projects")).data);

  useEffect(() => { loadProjects(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view, filterProject]);

  const openNew = () => {
    setEditing(null);
    setForm({ title: "", description: "", priority: "none", status: "todo", due_date: "", project_id: "", tags: "" });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      title: t.title, description: t.description || "", priority: t.priority, status: t.status,
      due_date: t.due_date || "", project_id: t.project_id || "", tags: (t.tags || []).join(", "),
    });
    setOpen(true);
  };
  const save = async () => {
    const payload = {
      title: form.title.trim(),
      description: form.description || undefined,
      priority: form.priority,
      status: form.status,
      due_date: form.due_date || null,
      project_id: form.project_id || null,
      tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
    };
    if (!payload.title) return;
    if (editing) await api.patch(`/tasks/${editing.task_id}`, payload);
    else await api.post("/tasks", payload);
    setOpen(false); load(); loadProjects(); toast.success("Saved");
  };
  const toggle = async (t) => {
    const status = t.status === "done" ? "todo" : "done";
    await api.patch(`/tasks/${t.task_id}`, { status });
    load();
  };
  const del = async (t) => {
    await api.delete(`/tasks/${t.task_id}`);
    load();
  };

  const addProject = async () => {
    const name = window.prompt("Project name");
    if (!name) return;
    await api.post("/projects", { name });
    loadProjects();
  };

  const renameProject = async (p) => {
    const name = window.prompt("Rename project", p.name);
    if (!name || name === p.name) return;
    await api.patch(`/projects/${p.project_id}`, { name, color: p.color, icon: p.icon });
    loadProjects();
  };
  const deleteProject = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"? Tasks will remain but become project-less.`)) return;
    await api.delete(`/projects/${p.project_id}`);
    if (filterProject === p.project_id) { setFilterProject("all"); setView("today"); }
    loadProjects();
  };

  const kanban = STATUSES.map(s => ({ ...s, items: tasks.filter(t => t.status === s.v) }));

  return (
    <div className="min-h-screen px-10 pt-10 pb-16 relative">
      <div className="flex items-baseline justify-between mb-6">
        <h1 className="serif-italic text-5xl text-white/85">Tasks</h1>
        <button onClick={openNew} className="pill" data-testid="new-task-button" style={{ background: "var(--orange)", borderColor: "var(--orange)" }}>
          <Plus size={13} /> New task
        </button>
      </div>
      <div className="h-px bg-white/10 mb-6" />

      <div className="grid grid-cols-[220px_1fr] gap-8">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Views</div>
          {["today", "overdue", "week", "inbox", "all"].map(v => (
            <button key={v} onClick={() => { setView(v); setFilterProject("all"); }} data-testid={`view-${v}`} className={`side-item w-full ${view === v && filterProject === "all" ? "active" : ""}`}>
              <span className="side-dot" /> {v === "all" ? "All tasks" : v[0].toUpperCase() + v.slice(1)}
            </button>
          ))}
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2 mt-6 flex items-center justify-between">Projects <button onClick={addProject} className="text-white/60 hover:text-white" data-testid="add-project"><Plus size={12} /></button></div>
          {projects.map(p => (
            <div key={p.project_id} className="group flex items-center">
              <button onClick={() => { setView("project"); setFilterProject(p.project_id); }} className={`side-item flex-1 ${filterProject === p.project_id ? "active" : ""}`}>
                <span className="side-dot" style={{ background: p.color }} /> {p.name} <span className="ml-auto text-xs text-white/40">{p.task_count}</span>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger data-testid={`project-menu-${p.project_id}`} className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-white px-2"><MoreHorizontal size={14} /></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[color:var(--surface)] border-white/10">
                  <DropdownMenuItem onClick={() => renameProject(p)} data-testid={`project-edit-${p.project_id}`}><Pencil size={13} className="mr-2" /> Rename</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => deleteProject(p)} data-testid={`project-delete-${p.project_id}`} className="text-red-400 focus:text-red-300"><Trash2 size={13} className="mr-2" /> Delete</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>

        <div>
          <Tabs defaultValue="list">
            <TabsList className="bg-white/5">
              <TabsTrigger value="list" data-testid="tab-list">List</TabsTrigger>
              <TabsTrigger value="kanban" data-testid="tab-kanban">Kanban</TabsTrigger>
              <TabsTrigger value="matrix" data-testid="tab-matrix">Matrix</TabsTrigger>
            </TabsList>
            <TabsContent value="list" className="mt-4 border border-white/10 rounded-xl overflow-hidden bg-[color:var(--surface)]">
              {tasks.length === 0 && <div className="text-white/40 p-10 text-center serif-italic text-xl">Nothing here.</div>}
              {tasks.map(t => <TaskRow key={t.task_id} task={t} onToggle={toggle} onDelete={del} onEdit={openEdit} />)}
            </TabsContent>
            <TabsContent value="kanban" className="mt-4">
              <div className="flex gap-4 overflow-x-auto pb-4">
                {kanban.map(col => (
                  <div key={col.v} className="kb-col shrink-0" data-testid={`kanban-col-${col.v}`}>
                    <div className="flex justify-between mb-3 text-sm text-white/70">{col.label} <span className="text-white/40">{col.items.length}</span></div>
                    <div className="space-y-2">
                      {col.items.map(t => (
                        <button key={t.task_id} onClick={() => openEdit(t)} className="block w-full text-left card-base p-3 text-sm">
                          <div className="text-white/90">{t.title}</div>
                          {t.due_date && <div className="text-xs text-white/40 mt-1">{format(parseISO(t.due_date), "MMM d")}</div>}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="matrix" className="mt-4">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { key: "urgent_important", label: "Urgent & Important", pri: ["urgent"] },
                  { key: "not_urgent_important", label: "Important, not urgent", pri: ["high"] },
                  { key: "urgent_not_important", label: "Urgent, not important", pri: ["medium"] },
                  { key: "neither", label: "Neither", pri: ["low", "none"] },
                ].map(q => (
                  <div key={q.key} className="card-base p-4 min-h-[220px]" data-testid={`matrix-${q.key}`}>
                    <div className="text-sm text-white/70 mb-2">{q.label}</div>
                    <div className="space-y-2">
                      {tasks.filter(t => q.pri.includes(t.priority) && t.status !== "done").map(t => (
                        <button key={t.task_id} onClick={() => openEdit(t)} className="block w-full text-left text-sm text-white/80 hover:text-white">• {t.title}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--surface)] border-white/10 max-w-lg">
          <DialogHeader><DialogTitle className="serif-italic text-2xl">{editing ? "Edit task" : "New task"}</DialogTitle></DialogHeader>
          <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="What needs doing?" className="bg-white/5 border-white/10" data-testid="task-title" />
          <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" rows={3} className="bg-white/5 border-white/10" />
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
              <SelectTrigger className="bg-white/5 border-white/10" data-testid="task-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="bg-white/5 border-white/10 hidden" />
            <div><DatePicker value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} placeholder="Due date" testId="task-due" /></div>
            <Select value={form.project_id || "none"} onValueChange={v => setForm({ ...form, project_id: v === "none" ? "" : v })}>
              <SelectTrigger className="bg-white/5 border-white/10"><SelectValue placeholder="Project" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No project</SelectItem>
                {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="tags, comma, separated" className="bg-white/5 border-white/10" />
          <div className="flex justify-end"><Button onClick={save} style={{ background: "var(--orange)" }} data-testid="task-save">Save</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
